import AppSidebar from '@/components/AppSidebar'

/**
 * DOM id of the board leftnav region. The top-bar toggle's `aria-controls`
 * points here, so it's exported to keep the two in sync. Lives on the
 * always-mounted outer wrapper (below) so the reference never dangles when the
 * rail is collapsed.
 */
export const BOARD_LEFTNAV_ID = 'board-leftnav'


interface BoardLeftNavRailProps {
  /** Whether the leftnav rail is expanded. */
  open: boolean
}

/**
 * Board leftnav rail (FS-513). A **push-layout** sibling of the board area:
 * when expanded it occupies its 80px width inline, shifting the canvas (and its
 * toolbar / zoom / add-files controls) to the right so everything stays
 * visible — it never overlays them. Collapsing returns the board to full width.
 *
 * Purely presentational: expand/collapse is driven entirely by the single
 * top-left toggle in {@link BoardTopBarV2} (whose icon flips with the state),
 * and the toggle handler in BoardCanvas owns the analytics. The rail carries no
 * controls, no backdrop, and no Esc handler, so the canvas stays fully
 * interactive while it's open.
 *
 * Board-scoped: other V2 screens mount AppSidebar permanently and never render
 * this rail.
 */
export default function BoardLeftNavRail({ open }: BoardLeftNavRailProps) {
  return (
    <div
      id={BOARD_LEFTNAV_ID}
      className={`relative h-full shrink-0 overflow-hidden transition-[width] duration-200 ease-out ${
        open ? 'w-20' : 'w-0'
      }`}
    >
      {/* AppSidebar is a fixed 80px rail pinned to the inline-start edge
          (`start-0` flips correctly under RTL); the wrapper's animated width
          clips it to 0 when collapsed, so it slides in as a push (the board
          area reflows to the remaining width) and the canvas regains full
          width on collapse. Mounted only while open so its focusable controls
          aren't reachable behind a zero-width box. */}
      {open && (
        <div className="absolute inset-y-0 start-0 w-20">
          <AppSidebar />
        </div>
      )}
    </div>
  )
}
